/**
 * React Router definition
 */
import { Navigate, useRoutes } from 'react-router-dom';

// Page layouts
import UserLayout from './layouts/dashboard/UserLayout';
import AdminLayout from './layouts/dashboard/AdminLayout';
import SimpleLayout from './layouts/simple';

// Initial pages
import LoginPage from './pages/LoginPage';
import RegistrationPage from './pages/RegistrationPage';
import ResetForgetPasswordPage from './pages/ResetForgetPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Page404 from './pages/Page404';
import Maintenance from './pages/Maintenance';
import UnsubscribePage from './pages/UnsubscribePage';
import RequirePermission from './components/require-permission';

// User section pages
import UserDashboard from './pages/user/Dashboard';
import UserEvents from './pages/user/events/Events';
import NewsletterPage from './pages/user/newsletter/NewsletterPage';
import ProfilePage from './pages/user/profile/ProfilePage';
import UserOrganization from './pages/user/orgStructure/UserOrganization';
import UserFederation from './pages/user/orgStructure/orgTree/UserFederation';
import UserFederationDetail from './pages/user/orgStructure/orgTree/UserFederationDetail';
import UserFederationChild from './pages/user/orgStructure/orgTree/UserFederationChild';
import UserPositionDetail from './pages/user/orgStructure/orgTree/UserPositionDetail';

// Admin section pages
import AdminDashboard from './pages/admin/Dashboard';
import Organization from './pages/admin/orgStructure/Organization';
import Users from './pages/admin/userList/UserList';
import Cards from './pages/admin/userCards/Cards';
import Newsletter from './pages/admin/newsletter/Newsletter';
import NewsletterNew from './pages/admin/newsletter/NewsletterNew';
import NewsletterDetail from './pages/admin/newsletter/NewsletterDetail';
import NewsletterTracking from './pages/admin/newsletter/NewsletterTracking';
import NewsletterReport from './pages/admin/newsletter/NewsletterReport';
import NewsletterTemplates from './pages/admin/newsletter/templates/NewsletterTemplates';
import NewsletterTemplateDetail from './pages/admin/newsletter/templates/NewsletterTemplateDetail';
import Events from './pages/admin/events/Events';
import EventsHandler from './utils/Events/EventsHandler';
import Campaigns from './pages/admin/campaigns/Campaigns';
import CampaignDetails from './pages/admin/campaigns/campaignDetails/CampaignDetails';
import FederationDetail from './pages/admin/orgStructure/OrgTree/FederationDetail';
import UserDetails from './pages/admin/userDetails/UserDetails';
import FederationUser from './pages/admin/orgStructure/OrgTree/FederationUser';
import PositionDetail from './pages/admin/orgStructure/OrgTree/PositionDetail';
import FederationChild from './pages/admin/orgStructure/OrgTree/FederationChild';
import Federation from './pages/admin/orgStructure/OrgTree/Federation';
import BankTransfers from './pages/admin/bankTransfer/BankTransfers';
import BankTransfersDetail from './pages/admin/bankTransfer/BankTransfersDetail';
import BankPaymentBatch from './pages/admin/bankPayment/BankPaymentBatch';
import BankPaymentBatchDetail from './pages/admin/bankPayment/BankPaymentBatchDetail';
import UserData from './pages/admin/userData/UserData';
import CityFiscalCode from './pages/admin/cityFiscalCode/CityFiscalCode';
import DataImport from './pages/admin/import/DataImport';
import Filters from './pages/admin/filters/Filters';
import FilterDetails from './pages/admin/filters/FilterDetails';

// Pagine newsletter: accessibili solo con il flag can_manage_newsletters (come la voce di menù).
const withNewsletterAuth = (page) => <RequirePermission auth="can_manage_newsletters">{page}</RequirePermission>;

/**
 * @returns Router definitions
 */
export default function Router() {
 const routes = useRoutes([
    {
      path: '/user',
      element: <UserLayout />,
      children: [
        { path: 'dashboard', element: <UserDashboard /> },
        { path: 'profile', element: <ProfilePage /> },
        { path: 'newsletter', element: <NewsletterPage /> },
        { path: 'organization', element: <UserOrganization /> },
        { path: 'organization/federations', element: <UserFederation /> },
        { path: 'organization/federations/federation-details', element: <UserFederationDetail /> },
        { path: 'organization/federations/federation-child', element: <UserFederationChild /> },
        { path: 'organization/charge-details', element: <UserPositionDetail /> },
        { path: 'events', element: <UserEvents /> },
        { path: 'events/handler', element: <EventsHandler /> },
      ],
    },
    {
      path: '/admin',
      element: <AdminLayout />,
      children: [
        { path: 'dashboard', element: <AdminDashboard /> },
        { path: 'organization', element: <Organization /> },
        { path: 'organization/federations', element: <Federation /> },
        { path: 'organization/federations/federation-details', element: <FederationDetail /> },
        { path: 'organization/federations/federation-child', element: <FederationChild /> },
        { path: 'organization/federations/users', element: <FederationUser /> },
        { path: 'organization/charge-details', element: <PositionDetail /> },
        { path: 'users', element: <Users /> },
        { path: 'users/user-details', element: <UserDetails /> },
        { path: 'users', element: <Users /> },
        { path: 'cards', element: <Cards /> },
        { path: 'newsletter', element: withNewsletterAuth(<Newsletter />) },
        { path: 'newsletter/new', element: withNewsletterAuth(<NewsletterNew />) },
        { path: 'newsletter/newsletter-details', element: withNewsletterAuth(<NewsletterDetail />) },
        { path: 'newsletter/tracking', element: withNewsletterAuth(<NewsletterTracking />) },
        { path: 'newsletter/report', element: withNewsletterAuth(<NewsletterReport />) },
        { path: 'newsletter/templates', element: withNewsletterAuth(<NewsletterTemplates />) },
        { path: 'newsletter/templates/detail', element: withNewsletterAuth(<NewsletterTemplateDetail />) },
        { path: 'campaigns', element: <Campaigns /> },
        { path: 'campaigns/campaign-details', element: <CampaignDetails /> },
        { path: 'bank-transfers', element: <BankTransfers /> },
        { path: 'bank-transfers/details', element: <BankTransfersDetail /> },
        { path: 'bankpayment', element: <BankPaymentBatch /> },
        { path: 'bankpayment/details', element: <BankPaymentBatchDetail /> },
        { path: 'userdata', element: <UserData /> },
        { path: 'cityfiscalcode', element: <CityFiscalCode /> },
        { path: 'import', element: <DataImport /> },
        { path: 'events', element: <Events /> },
        { path: 'events/handler', element: <EventsHandler /> },
        { path: 'filters', element: <Filters /> },
        { path: 'filters/filter-details', element: <FilterDetails /> },
      ],
    },
    {
      path: '/',
      element: <LoginPage />,
    },
    {
      path: '/join',
      element: <RegistrationPage key="join" />,
    },
    {
      // Flusso GD-only: stesso motore di registrazione in modalità GD (sprint 019).
      // key distinto da /join → passando da /join a /join-gd il componente si rimonta,
      // così l'init dello stato riparte (step 1 in GD) senza flash della landing.
      path: '/join-gd',
      element: <RegistrationPage key="join-gd" mode="GD" />,
    },
    {
      path: '/forgetPassword',
      element: <ResetForgetPasswordPage />,
    },
    {
      path: '/resetPassword',
      element: <ResetPasswordPage />,
    },
    {
      path: '/newsletter/unsubscribe',
      element: <UnsubscribePage />,
    },
    {
      path: '/newsletter/resubscribe',
      element: <UnsubscribePage mode="resubscribe" />,
    },
    {
      path: '/logout',
      element: <Navigate to="/" />,
    },
    {
      element: <Maintenance/>,
      children: [
        { path: '/maintenance', element: <Maintenance/>},
        { path: '*', element: <Navigate to="/maintenance" /> },
      ]
    },
  ]);

  return routes;
}
